// ============================================================
// GitRebaseMerge.jsx
//
// A branded, responsive course content page for the
// "Git Merge vs. Rebase" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: History integration strategies, linear vs. non-linear
//           workflows, and the Golden Rule of Rebasing.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import MergeTypeIcon          from "@mui/icons-material/MergeType";
import CallMergeIcon          from "@mui/icons-material/CallMerge";
import TimelineIcon           from "@mui/icons-material/Timeline";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import WarningAmberIcon       from "@mui/icons-material/WarningAmber";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitRebaseMerge() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<MergeTypeIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Advanced Workflows"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Merge vs. <span>Rebase</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Two paths to the same destination. Both merge and rebase integrate changes from one branch into another, but they do so by either preserving the messiness of history or rewriting it for clarity.
                    </Typography>
                </Box>

                {/* 1. MERGE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CallMergeIcon />} label="1. The Merge Strategy" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        A merge creates a new <strong>Merge Commit</strong>. It acts as a bridge that ties two separate histories together, preserving the full context of when and where development occurred.
                    </Typography>
                    <CodeBlock
                        title="Standard Merge"
                        code={`git switch main\ngit merge feature-branch\n\n# Result: A non-linear history with a dedicated merge commit.`}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, background: "rgba(0, 255, 0, 0.05)", border: `1px solid rgba(0, 255, 0, 0.2)`, borderRadius: 2 }}>
                                <Typography sx={{ color: "#4caf50", fontWeight: 700, fontSize: 13, mb: 1 }}>✅ Pros</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 11 }}>Non-destructive; history is never rewritten. Safe for public, shared branches.</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, background: "rgba(255, 0, 0, 0.05)", border: `1px solid rgba(255, 0, 0, 0.2)`, borderRadius: 2 }}>
                                <Typography sx={{ color: "#f44336", fontWeight: 700, fontSize: 13, mb: 1 }}>⚠️ Cons</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 11 }}>Can clutter the git log with many "Merge branch..." commits if used frequently.</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 2. REBASE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TimelineIcon />} label="2. The Rebase Strategy" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Rebasing <strong>rewrites</strong> history. It moves the base of your feature branch to the tip of the target branch, creating a perfectly linear timeline.
                    </Typography>
                    <CodeBlock
                        title="Standard Rebase"
                        code={`git switch feature-branch\ngit rebase main\n\n# Result: Changes are placed on top of main as if they were written there.`}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, background: "rgba(0, 255, 0, 0.05)", border: `1px solid rgba(0, 255, 0, 0.2)`, borderRadius: 2 }}>
                                <Typography sx={{ color: "#4caf50", fontWeight: 700, fontSize: 13, mb: 1 }}>✅ Pros</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 11 }}>Clean, linear log. Eliminates unnecessary merge commits for a professional history.</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, background: "rgba(255, 0, 0, 0.05)", border: `1px solid rgba(255, 0, 0, 0.2)`, borderRadius: 2 }}>
                                <Typography sx={{ color: "#f44336", fontWeight: 700, fontSize: 13, mb: 1 }}>⚠️ Cons</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 11 }}>Dangerous on public branches. Rewriting history can cause chaos for teammates.</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 3. THE GOLDEN RULE */}
                <Box sx={{ mb: 6, p: 4, background: "rgba(218,165,32,0.08)", border: `2px dashed ${BRAND.gold}`, borderRadius: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <WarningAmberIcon sx={{ color: BRAND.gold, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ ...sectionHeading, color: BRAND.goldBright }}>The Golden Rule of Rebasing</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.white, fontSize: 14, fontWeight: 500 }}>
                        Never rebase a branch that has been <strong>pushed to a remote repository</strong> and is being used by other people.
                        <br /><br />
                        If you rewrite the history of a shared branch, your teammates will have a divergent history, leading to complex and painful "forced push" conflicts that are difficult to resolve.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="When to use What?" />
                    <Grid container spacing={1.5}>
                        {[
                            "Use Merge for public/shared branches to preserve development context.",
                            "Use Rebase for local feature branches to keep your team's log clean.",
                            "Rebase frequently on local branches to catch upstream conflicts early.",
                            "Squash commits during a rebase to turn 10 small commits into 1 clean feature.",
                            "Avoid rebasing after you have opened a Pull Request for review.",
                            "Default to Merge if you are unsure; it is always the safer, non-destructive path."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Want the best of both worlds? Many teams use <strong>Interactive Rebase</strong> (<code>git rebase -i</code>) to clean up their local commits before doing a final <strong>Merge</strong> into the main branch!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
